import { useEffect, useState } from "react";
import { api, errDetail, fmtDate, fmtEur } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { TranslateBlock } from "./TranslateBlock";
import {
  Sparkles, Loader2, TrendingUp, Activity, Users, Wallet, AlertTriangle,
  ListChecks, Globe, ShieldCheck, ShieldAlert, FileDown, ExternalLink, Newspaper, LineChart as LineIcon,
} from "lucide-react";

const RISK_COLOR = { green: "#10b981", yellow: "#f59e0b", orange: "#f97316", red: "#ef4444" };
const DIR_KEY = { wachsend: "dir_growing", stabil: "dir_stable", rückläufig: "dir_declining" };

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3 text-[#1B2A4A]"><Icon className="h-4 w-4" />{title}</h3>
      {children}
    </div>
  );
}

function Sourced({ url, date, t }) {
  return (
    <span className="text-[11px] text-blue-600 inline-flex items-center gap-1">
      {url && <a href={url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-0.5"><ExternalLink className="h-3 w-3" />{t("ext_source")}</a>}
      <span className="text-gray-400">· {date || t("ext_no_date")}</span>
    </span>
  );
}

export function AccountAnalysis({ accountId }) {
  const { t, hasIntel } = useApp();
  const [analysis, setAnalysis] = useState(null);
  const [web, setWeb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [webBusy, setWebBusy] = useState(false);

  const load = () => {
    api.get(`/accounts/${accountId}/analysis`).then((r) => setAnalysis(r.data)).catch(() => {});
    api.get(`/accounts/${accountId}/web-research`).then((r) => setWeb(r.data)).catch(() => {});
  };
  useEffect(() => { if (hasIntel) load(); /* eslint-disable-next-line */ }, [accountId, hasIntel]);

  if (!hasIntel) return <p className="text-sm text-gray-400 py-8 text-center" data-testid="intel-denied-analysis">{t("access_denied_intel")}</p>;

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/accounts/${accountId}/analysis/generate`); setAnalysis(r.data); toast.success(t("ws_saved")); }
    catch (e) { toast.error(errDetail(e)); }
    setBusy(false);
  };
  const research = async () => {
    setWebBusy(true);
    try { const r = await api.post(`/accounts/${accountId}/web-research/generate`); setWeb(r.data); toast.success(t("ws_saved")); }
    catch (e) { toast.error(errDetail(e)); }
    setWebBusy(false);
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/accounts/${accountId}/analysis/pdf`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "Account_Analyse.pdf"; a.click();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const hasAnalysis = analysis && analysis.generated_at;
  const nar = (analysis && analysis.narrative) || {};
  const trend = (analysis && analysis.trend) || {};
  const sentiment = (analysis && analysis.sentiment) || {};
  const wd = (web && web.data) || null;

  return (
    <div className="space-y-4" data-testid="account-analysis-panel">
      {/* Header / actions */}
      <div className="bg-[#1B2A4A] rounded-lg p-4 flex flex-wrap items-center gap-3 text-white">
        <Sparkles className="h-5 w-5 text-[#F5A623]" />
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-semibold">{t("ai_analysis")}</p>
          <p className="text-xs text-white/60">{hasAnalysis ? `${t("last_generated")}: ${fmtDate(analysis.generated_at)} · ${analysis.generated_by}` : t("ai_never_run")}</p>
        </div>
        {hasAnalysis && <Button size="sm" variant="secondary" onClick={downloadPdf} data-testid="analysis-pdf-btn"><FileDown className="h-3.5 w-3.5 mr-1" />{t("analysis_pdf")}</Button>}
        <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A]" onClick={generate} disabled={busy} data-testid="generate-analysis-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
          {hasAnalysis ? t("update_now") : t("generate_now")}
        </Button>
      </div>

      {busy && <div className="flex items-center justify-center gap-2 py-8 text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-[#F5A623]" />{t("generating")}</div>}

      {hasAnalysis && !busy && (
        <>
          <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 gap-1"><ShieldCheck className="h-3 w-3" />{t("internal_verified")}</Badge>

          {/* Trend */}
          <Section icon={TrendingUp} title={t("ai_trend")} testid="section-trend">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{t("ai_direction")}: {t(DIR_KEY[trend.direction] || "dir_stable")}</Badge>
              {(trend.jumps || []).map((j, i) => <Badge key={i} className={`text-xs ${j.change_pct < 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"} hover:bg-inherit`}>{j.month}: {j.change_pct > 0 ? "+" : ""}{j.change_pct}%</Badge>)}
            </div>
            {(trend.series || []).length > 1 && (
              <div className="h-48 mb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend.series} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Bar dataKey="revenue" fill="#1B2A4A" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <TranslateBlock texts={[nar.trend_narrative || t("ai_no_data")]} testid="analysis-trend-translate">
              {(display, button) => (
                <>
                  {nar.trend_narrative && <div className="flex justify-end mb-1">{button}</div>}
                  <p className="text-sm text-gray-700">{display[0]}</p>
                </>
              )}
            </TranslateBlock>
          </Section>

          {/* Sentiment */}
          <Section icon={Activity} title={t("ai_sentiment")} testid="section-sentiment">
            {(sentiment.timeline || []).length > 0 ? (
              <>
                <div className="flex flex-wrap items-center gap-1 mb-2">
                  {sentiment.timeline.map((s, i) => (
                    <span key={i} className="h-3 w-3 rounded-full" title={`${s.date}: ${s.reason}`} style={{ background: RISK_COLOR[s.risk_level] || "#ccc" }} />
                  ))}
                </div>
                <div className="h-32 mb-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sentiment.timeline} margin={{ top: 5, right: 10, bottom: 0, left: -30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} /><YAxis domain={[0, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 9 }} />
                      <Tooltip /><Line type="stepAfter" dataKey="score" stroke="#F5A623" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : <p className="text-xs text-gray-400 mb-2">{t("ai_no_data")}</p>}
            <p className="text-sm text-gray-700">{nar.sentiment_narrative || t("ai_no_data")}</p>
          </Section>

          {/* Stakeholders */}
          <Section icon={Users} title={t("ai_stakeholders")} testid="section-stakeholders">
            {(nar.stakeholders || []).length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-2">
                {nar.stakeholders.map((s, i) => (
                  <div key={i} className="border border-gray-100 rounded p-2 text-sm">
                    <p className="font-medium">{s.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant="outline" className="text-[11px]">{s.role_influence}</Badge>
                      <Badge className="text-[11px] bg-gray-100 text-gray-700 hover:bg-gray-100">{s.stance}</Badge>
                    </div>
                    {s.note && <p className="text-xs text-gray-500 mt-1">{s.note}</p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400">{t("ai_no_data")}</p>}
            {(nar.stakeholder_flags || []).map((f, i) => (
              <p key={i} className="text-xs text-amber-700 flex items-center gap-1 mt-2"><AlertTriangle className="h-3.5 w-3.5" />{f}</p>
            ))}
          </Section>

          {/* Wallet */}
          <Section icon={Wallet} title={t("ai_wallet")} testid="section-wallet">
            <p className="text-sm text-gray-700">{nar.wallet_narrative || t("ai_no_data")}</p>
          </Section>

          {/* Risks & opportunities */}
          <Section icon={AlertTriangle} title={t("ai_risks_opps")} testid="section-risks">
            {(nar.risks_opportunities || []).length > 0 ? (
              <ul className="space-y-1.5">
                {nar.risks_opportunities.map((r, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Badge className={`text-[10px] shrink-0 ${r.type === "risk" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}`}>{r.type === "risk" ? t("risk_label") : t("chance_label")}</Badge>
                    <span className="text-gray-700">{r.text}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-gray-400">{t("ai_no_data")}</p>}
          </Section>

          {/* Recommendations */}
          <Section icon={ListChecks} title={t("ai_recommendations")} testid="section-recommendations">
            {(nar.recommendations || []).length > 0 ? (
              <ol className="space-y-1.5">
                {nar.recommendations.slice().sort((a, b) => (a.priority || 9) - (b.priority || 9)).map((r, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="h-5 w-5 shrink-0 rounded-full bg-[#F5A623] text-[#1B2A4A] text-xs font-bold flex items-center justify-center">{r.priority || i + 1}</span>
                    <span className="text-gray-700">{r.action}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-gray-400">{t("ai_no_data")}</p>}
          </Section>
        </>
      )}

      {/* External research */}
      <div className="bg-white border-2 border-dashed border-blue-200 rounded-lg p-4" data-testid="external-research">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Globe className="h-4 w-4 text-blue-600" />
          <div className="flex-1 min-w-[160px]">
            <p className="text-sm font-semibold text-blue-900">{t("ext_research")}</p>
            <p className="text-xs text-blue-500">{web && web.researched_at ? `${t("last_generated")}: ${fmtDate(web.researched_at)}` : t("ext_never")}</p>
          </div>
          <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 gap-1 text-[11px]"><ShieldAlert className="h-3 w-3" />{t("ext_unverified")}</Badge>
          <Button size="sm" variant="outline" onClick={research} disabled={webBusy} data-testid="generate-web-btn">
            {webBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1" />}{t("ext_update")}
          </Button>
        </div>

        {webBusy && <div className="flex items-center justify-center gap-2 py-6 text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-blue-500" />{t("ext_researching")}</div>}

        {wd && !webBusy && (
          <div className="space-y-4">
            {wd.no_relevant_info && <p className="text-sm text-gray-500 italic">{t("ext_no_info")}</p>}

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1.5 flex items-center gap-1"><Newspaper className="h-3.5 w-3.5" />{t("ext_news")}</p>
              {(wd.company_news || []).length ? wd.company_news.map((n, i) => (
                <div key={i} className="mb-2 text-sm">
                  <p className="font-medium text-gray-800">{n.headline}</p>
                  <p className="text-gray-600 text-xs">{n.summary}</p>
                  <Sourced url={n.url} date={n.date} t={t} />
                </div>
              )) : <p className="text-xs text-gray-400">{t("ext_no_info")}</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1.5">{t("ext_financials")}</p>
              {wd.financials && wd.financials.summary ? (
                <div className="text-sm"><p className="text-gray-700">{wd.financials.summary}</p><Sourced url={wd.financials.url} date={wd.financials.date} t={t} /></div>
              ) : <p className="text-xs text-gray-400">{t("ext_no_info")}</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1.5 flex items-center gap-1"><LineIcon className="h-3.5 w-3.5" />{t("ext_strategy")}</p>
              {(wd.strategy || []).length ? <ul className="space-y-1">{wd.strategy.map((s, i) => (
                <li key={i} className="text-sm text-gray-700">• {s.point} <Sourced url={s.url} date={s.date} t={t} /></li>
              ))}</ul> : <p className="text-xs text-gray-400">{t("ext_no_info")}</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1.5">{t("ext_market_comp")}</p>
              {(wd.market_competitors || []).length ? <div className="flex flex-wrap gap-1.5">{wd.market_competitors.map((c, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-0.5" title={c.note}>{c.name}</span>
              ))}</div> : <p className="text-xs text-gray-400">{t("ext_no_info")}</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1.5">{t("ext_contact_check")}</p>
              {(wd.contact_check || []).length ? <ul className="space-y-1">{wd.contact_check.map((c, i) => (
                <li key={i} className="text-sm flex items-center gap-2">
                  <Badge className={`text-[10px] ${c.status === "abweichung" ? "bg-amber-100 text-amber-800" : c.status === "bestätigt" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"} hover:bg-inherit`}>{c.status}</Badge>
                  <span className="text-gray-700">{c.name}</span><span className="text-xs text-gray-400">{c.note}</span>
                </li>
              ))}</ul> : <p className="text-xs text-gray-400">{t("ext_no_info")}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
